import React from 'react';
import { toast } from 'sonner';

import { makeStyles } from '@material-ui/core/styles';

import Button from 'creative-components/CustomButtons/Button';

import CampaignTargetingSection, {
  type CampaignTargetingSectionHandle,
  type CampaignTargetingState,
  type CampaignTargetingSubmissionData,
} from 'components/CampaignTargetingSection/CampaignTargetingSection';
import { useDataFilterContext } from 'components/DataMap/DataFilterProvider';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import { useUserInfoContext } from 'components/UserInfoProvider/UserInfoProvider';

import { getSimpleCampaignCounts } from 'utils/api';

const useStyles = makeStyles(() => ({
  nextButton: {
    float: 'right',
    marginTop: '30px',
    marginBottom: '20px',
  },
}));

export type CampaignCreateData = CampaignTargetingSubmissionData;

type FetchCountArgs = {
  propertyFilters: any;
  polygonPaths?: { lat: number; lng: number }[][];
  zipcode?: string;
};

type Props = {
  handleNext: () => void;
  setCreateCampaignData: React.Dispatch<React.SetStateAction<CampaignCreateData | undefined>>;
  isAnnualBilling: boolean;
  setIsAnnualBilling: React.Dispatch<React.SetStateAction<boolean>>;
};

const SimpleCreateCampaignStep = ({
  handleNext,
  setCreateCampaignData,
  isAnnualBilling,
  setIsAnnualBilling,
}: Props) => {
  const classes = useStyles();
  const { userInfo } = useUserInfoContext();
  const {
    availableEventTypes, selectedEventType, setSelectedEventTypeByName,
  } = useDataFilterContext();

  const targetingRef = React.useRef<CampaignTargetingSectionHandle | null>(null);
  const [targetingState, setTargetingState] = React.useState<CampaignTargetingState>({
    apnAvailableCounts: [],
    apnSelectedCounts: [],
    allAvailableCount: 0,
    allApnAvailableCounts: [],
    apnsTotalInAreaCount: 0,
    apnsTotalSelectedCount: 0,
    farmAreaAllAvailableCount: 0,
    farmAreaHighPropensityAvailableCount: 0,
    hasFarmLimit: false,
    highPropensityAvailableCount: 0,
    highPropensityApnAvailableCounts: [],
    showHighPropensityTargeting: false,
    showOverlapWarning: false,
    useHighPropensityToSell: false,
  });

  React.useEffect(() => {
    if (!availableEventTypes) return;
    setSelectedEventTypeByName('Full Farm');
  }, [availableEventTypes]);
  if (!selectedEventType) return (<LoadingIndicator />);

  const onNext = () => {
    const targeting = targetingRef.current;
    if (!targeting) return;

    if (!targeting.areDataCountsUpToDate()) {
      toast.warning('Please click on the Generate Costs button to update the number of APNs in your latest farm area selection!');
      return;
    }

    const { apnsTotalSelectedCount } = targeting.getState();
    if (apnsTotalSelectedCount === 0) {
      toast.warning('There are no parcels in your selection area. Please select an area or expand your selection!');
      return;
    }

    setCreateCampaignData(targeting.getSubmissionData());

    handleNext();
  };

  return (
    <>
      <CampaignTargetingSection
        ref={targetingRef}
        selectedEventType={selectedEventType}
        customMonthlyRatePerApn={userInfo?.agentInfo.customMonthlyRatePerApn}
        isAnnualBilling={isAnnualBilling}
        setIsAnnualBilling={setIsAnnualBilling}
        fetchDataCount={({ propertyFilters, polygonPaths, zipcode }: FetchCountArgs) => getSimpleCampaignCounts({
          propertyFilters,
          polygonPaths,
          zipcode,
        })}
        onStateChange={setTargetingState}
        generateCostsButtonText="Generate Costs"
        disableFilters
        zipcodesFormLabel={undefined}
      />

      {targetingState.apnSelectedCounts.length > 0 && (
        <Button
          round
          color="primary"
          className={classes.nextButton}
          onClick={onNext}
        >
          Next
        </Button>
      )}
    </>
  );
};

export default SimpleCreateCampaignStep;
